import { diffLines } from '@fos/shared/contracts/context';
import type { KnowledgeItemDto } from '@fos/shared/contracts/knowledge';
import { History, RotateCcw } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { appErrorCode, errorMessage } from '../../../lib/api';
import { formatDateTimeShort } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Markdown } from '../../../ui/Markdown';
import { Segmented } from '../../../ui/Segmented';
import { Select } from '../../../ui/Select';
import { EmptyState, ErrorState, Skeleton } from '../../../ui/States';
import { toast } from '../../../ui/Toast';
import { DiffView } from './DiffView';

/**
 * Historia wersji rekordu wiedzy (MVP §8: autor, data, podgląd, przywrócenie). Przywrócenie starszej wersji tworzy
 * NOWĄ wersję z jej treścią — historia zostaje. „Zmiany” = różnice względem bieżącej treści.
 */
export function VersionsDialog({
  item,
  open,
  onOpenChange,
}: {
  item: KnowledgeItemDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const utils = trpc.useUtils();
  const versions = trpc.knowledge.listVersions.useQuery({ id: item.id }, { enabled: open });
  const [selected, setSelected] = useState<number | null>(null);
  const [view, setView] = useState<'content' | 'diff'>('content');
  const [error, setError] = useState<string | null>(null);

  // po otwarciu: poprzednia wersja (gdy jest), inaczej bieżąca
  useEffect(() => {
    if (!open) {
      setSelected(null);
      setError(null);
      setView('content');
      return;
    }
    if (selected === null && versions.data && versions.data.length > 0) {
      const previous = versions.data.find((v) => !v.current);
      setSelected((previous ?? versions.data[0])?.versionNo ?? null);
    }
  }, [open, versions.data, selected]);

  const version = trpc.knowledge.getVersion.useQuery(
    { id: item.id, versionNo: selected ?? 1 },
    { enabled: open && selected !== null },
  );

  const restore = trpc.knowledge.restoreVersion.useMutation({
    onSuccess: (updated) => {
      toast.success(`Przywrócono wersję ${selected}`, {
        description: `Zapisano jako wersję ${updated.currentVersion}.`,
      });
      void utils.knowledge.get.invalidate({ id: item.id });
      void utils.knowledge.listVersions.invalidate({ id: item.id });
      void utils.knowledge.list.invalidate();
      onOpenChange(false);
    },
    onError: (err) => {
      setError(
        appErrorCode(err) === 'CONFLICT'
          ? 'Ktoś zmienił ten rekord w międzyczasie. Zamknij okno i otwórz historię ponownie.'
          : errorMessage(err),
      );
    },
  });

  let content: ReactNode;
  if (versions.isPending) {
    content = (
      <div role="status" aria-label="Wczytywanie" className="flex flex-col gap-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  } else if (versions.isError) {
    content = <ErrorState compact onRetry={() => void versions.refetch()} retrying={versions.isFetching} />;
  } else if (versions.data.length === 0) {
    content = <EmptyState compact icon={History} title="Brak wersji" />;
  } else {
    const current = version.data;
    content = (
      <div className="flex flex-col gap-4">
        {error && <FormMessage>{error}</FormMessage>}
        <Field label="Wersja">
          <Select
            value={selected !== null ? String(selected) : undefined}
            onValueChange={(v) => {
              setSelected(Number(v));
              setError(null);
            }}
            options={versions.data.map((v) => ({
              value: String(v.versionNo),
              label: `Wersja ${v.versionNo}${v.current ? ' · bieżąca' : ''} · ${formatDateTimeShort(v.createdAt)}${v.author ? ` · ${v.author.name}` : ''}`,
            }))}
          />
        </Field>
        <div className="flex justify-end">
          <Segmented
            aria-label="Widok wersji"
            size="s"
            value={view}
            onValueChange={setView}
            options={[
              { value: 'content', label: 'Treść' },
              { value: 'diff', label: 'Zmiany względem bieżącej' },
            ]}
          />
        </div>
        <div className="max-h-[45dvh] overflow-y-auto">
          {version.isPending ? (
            <Skeleton className="h-32 w-full" />
          ) : version.isError ? (
            <ErrorState compact onRetry={() => void version.refetch()} retrying={version.isFetching} />
          ) : current && view === 'content' ? (
            current.bodyMd.trim() ? (
              <div className="rounded-lg border border-line p-4">
                <Markdown className="text-body-s">{current.bodyMd}</Markdown>
              </div>
            ) : (
              <p className="text-body-s text-fg-muted">Ta wersja nie ma treści.</p>
            )
          ) : current ? (
            <DiffView
              lines={diffLines(item.bodyMd, current.bodyMd)}
              emptyText="Treść tej wersji jest taka sama jak bieżąca."
            />
          ) : null}
        </div>
      </div>
    );
  }

  const selectedIsCurrent = selected === null || versions.data?.find((v) => v.versionNo === selected)?.current;
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title="Historia wersji"
      description="Każdy zapis treści to nowa wersja z autorem i datą."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Zamknij
          </Button>
          {item.canEdit && (
            <Button
              variant="primary"
              icon={RotateCcw}
              disabled={Boolean(selectedIsCurrent) || !version.data}
              loading={restore.isPending}
              onClick={() =>
                selected !== null && restore.mutate({ id: item.id, versionNo: selected, version: item.version })
              }
            >
              Przywróć jako nową wersję
            </Button>
          )}
        </>
      }
    >
      {content}
    </Dialog>
  );
}
